/**
 * /collections/:id — single Подборка.
 *
 * Member list (like the wizard's cart), summary stats, and the same launch/
 * extend CTAs as the list page card. Bloggers can be removed inline.
 */
import { useNavigate, useParams, Link } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Download, Eye, FolderInput, Globe, Plus, Rocket, Sparkles, Target, Users, Wallet, X } from "lucide-react";
import {
  useBloggers,
  useCampaigns,
  useCollection,
  useKanban,
  useLaunchCollection,
  usePatchCollection,
  useRemoveBloggerFromCollection,
  useAddBloggersToCollection,
  exportCollectionXlsx,
  type AiBrief,
  type Blogger,
  type CampaignSummary,
} from "@/shared/api/wb";
import { Avatar, Button, Card, PlatformIcon, Skeleton, type Platform } from "@/shared/ui";
import { fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { ExtendToCampaignModal } from "./Collections";

export function CollectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data } = useCollection(id ?? null);
  const { data: bloggers } = useBloggers({});
  const launch = useLaunchCollection();
  const patch = usePatchCollection();
  const remove = useRemoveBloggerFromCollection();
  const [extOpen, setExtOpen] = useState(false);
  const [extBloggerIds, setExtBloggerIds] = useState<string[] | undefined>(undefined);
  const [fromCampOpen, setFromCampOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [exporting, setExporting] = useState(false);

  function openExtForAll() {
    setExtBloggerIds(undefined);
    setExtOpen(true);
  }

  function openExtForBlogger(bloggerId: string) {
    setExtBloggerIds([bloggerId]);
    setExtOpen(true);
  }

  if (!data) return <div className="p-9 text-ink-40">Загрузка…</div>;

  const memberById = new Map((bloggers ?? []).map((b) => [b.id, b]));
  const members = data.members.map((m) => memberById.get(m.blogger_id)).filter(Boolean) as NonNullable<ReturnType<typeof memberById.get>>[];
  const currentMemberIds = new Set(data.members.map((m) => m.blogger_id));
  const noteById = new Map(data.members.map((m) => [m.blogger_id, m.note]));

  async function onLaunch() {
    const res = await launch.mutateAsync(data!.id);
    navigate(res.wizard_step || "/campaign/new/product");
  }

  async function onExport() {
    setExporting(true);
    try { await exportCollectionXlsx(data!.id); }
    finally { setExporting(false); }
  }

  function openRename() {
    setRenameValue(data!.name);
    setRenameOpen(true);
  }

  function confirmRename() {
    const v = renameValue.trim();
    if (v) patch.mutate({ id: data!.id, patch: { name: v } });
    setRenameOpen(false);
  }

  return (
    <div className="p-7 pb-16 max-w-[1100px] mx-auto">
      <Link to="/collections" className="inline-flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink mb-2">
        <ArrowLeft size={13} /> Все подборки
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="font-display font-extrabold text-[28px] tracking-tight truncate">{data.name}</h1>
            <button onClick={openRename} className="text-[12px] text-link hover:underline">переименовать</button>
            {data.is_active && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[10px] font-mono font-bold">АКТИВНАЯ</span>
            )}
          </div>
          <div className="text-[13px] text-ink-40">{members.length} {pluralBloggers(members.length)} · базовая сумма размещений {fmtRubFull(data.base_total_price)}</div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setFromCampOpen(true)}>
            <FolderInput size={13} /> Из кампании
          </Button>
          <Button variant="outline" size="sm" onClick={openExtForAll}>
            <Plus size={13} /> Добавить в РК
          </Button>
          <Button variant="outline" size="sm" onClick={onExport} disabled={exporting || members.length === 0}>
            <Download size={13} /> {exporting ? "Выгружаем…" : "Excel"}
          </Button>
          <Button onClick={onLaunch} disabled={launch.isPending || members.length === 0}>
            <Rocket size={14} /> Запустить РК
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat icon={<Users size={14} />} label="Блогеров" value={String(members.length)} />
        <Stat icon={<Eye size={14} />} label="Совокупный охват" value={data.total_reach ? fmtNum(data.total_reach) : "—"} />
        <Stat icon={<Wallet size={14} />} label="Базовый бюджет" value={fmtRubFull(data.base_total_price)} accent />
      </div>

      {data.ai_brief && <AiBriefPanel brief={data.ai_brief} />}

      <Card className="!p-0">
        {members.length === 0 ? (
          <div className="text-center py-14 text-ink-40">
            <div className="text-[14px] mb-2">Подборка пуста</div>
            <div className="text-[12.5px]">Открой каталог и плюсуй блогеров — они появятся здесь.</div>
            <Link to="/catalog" className="inline-block mt-3 text-link text-[13px] hover:underline">К каталогу →</Link>
          </div>
        ) : (
          <div className="grid divide-y divide-ink-12/60">
            {members.map((b) => {
              const note = noteById.get(b.id);
              return (
                <div key={b.id} className="px-4 py-3">
                  <div className="grid grid-cols-[40px_1fr_120px_120px_auto] items-center gap-3">
                    <Avatar name={b.full} size={36} grad={b.grad as [string, string]} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 font-display font-semibold text-[14px]">
                        <PlatformIcon platform={b.platform as Platform} size={12} /> {b.name}
                      </div>
                      <div className="text-[11.5px] text-ink-40 truncate">{b.full} · {b.cats.slice(0, 2).join(", ")}</div>
                    </div>
                    <Cell label="охват" value={fmtNum(b.reach)} />
                    <Cell label="размещение" value={fmtRub(b.price)} />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openExtForBlogger(b.id)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:border-link hover:text-link transition-colors whitespace-nowrap">
                        <FolderInput size={12} /> В РК
                      </button>
                      <button onClick={() => remove.mutate({ id: data.id, blogger_id: b.id })}
                        className="w-7 h-7 grid place-items-center rounded-md text-ink-40 hover:text-danger hover:bg-danger/10">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  {note && (
                    <div className="mt-2 ml-[52px] pl-3 border-l-2 border-lime/60 text-[12.5px] text-ink-60 leading-relaxed">
                      <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-lime font-display font-bold mr-1.5 align-middle">
                        <Sparkles size={10} /> почему лично вам
                      </span>
                      {note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {extOpen && (
        <ExtendToCampaignModal
          collectionId={data.id}
          bloggerIds={extBloggerIds}
          onClose={() => { setExtOpen(false); setExtBloggerIds(undefined); }}
        />
      )}
      {fromCampOpen && (
        <AddFromCampaignModal
          collectionId={data.id}
          currentMemberIds={currentMemberIds}
          onClose={() => setFromCampOpen(false)}
        />
      )}
      {renameOpen && (
        <RenameModal
          value={renameValue}
          onChange={setRenameValue}
          onConfirm={confirmRename}
          onCancel={() => setRenameOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Rename modal ──────────────────────────────────────────────────────────────

function RenameModal({ value, onChange, onConfirm, onCancel }: {
  value: string; onChange: (v: string) => void;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg p-6 w-[min(400px,92vw)] fade-up">
        <div className="font-display font-bold text-[18px] mb-4">Переименовать подборку</div>
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onConfirm(); if (e.key === "Escape") onCancel(); }}
          className="w-full h-10 px-3 rounded-md border-2 border-ink-12 focus:border-ink outline-none text-[14px] font-display mb-5"
          placeholder="Название подборки"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel}
            className="h-9 px-4 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink">
            Отмена
          </button>
          <button onClick={onConfirm} disabled={!value.trim()}
            className="h-9 px-4 rounded-pill bg-ink text-white text-[13px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-40">
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add from campaign modal ───────────────────────────────────────────────────

type Step = { kind: "campaigns" } | { kind: "bloggers"; campaign: CampaignSummary };

function AddFromCampaignModal({ collectionId, currentMemberIds, onClose }: {
  collectionId: string;
  currentMemberIds: Set<string>;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>({ kind: "campaigns" });
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data: campaigns } = useCampaigns();
  const { data: allBloggers } = useBloggers({});
  const add = useAddBloggersToCollection();

  const campaignId = step.kind === "bloggers" ? step.campaign.id : null;
  const { data: kanban, isLoading: kanbanLoading } = useKanban(campaignId);

  const byId = new Map((allBloggers ?? []).map((b) => [b.id, b]));

  const eligible = (campaigns ?? []).filter((c) => c.status !== "draft");

  // Unique bloggers in the selected campaign, deduplicated by blogger_id
  const campaignBloggers: Blogger[] = step.kind === "bloggers" && kanban
    ? [...new Map(
        kanban.placements
          .map((p) => byId.get(p.blogger_id))
          .filter((b): b is Blogger => !!b)
          .map((b) => [b.id, b]),
      ).values()]
    : [];

  function toggle(bid: string) {
    if (currentMemberIds.has(bid)) return;
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(bid) ? next.delete(bid) : next.add(bid);
      return next;
    });
  }

  async function addPicked() {
    await add.mutateAsync({ id: collectionId, blogger_ids: [...picked] });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg w-[min(520px,92vw)] fade-up flex flex-col max-h-[80vh]">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-ink-12 flex-shrink-0">
          {step.kind === "bloggers" && (
            <button onClick={() => { setStep({ kind: "campaigns" }); setPicked(new Set()); }}
              className="flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink mb-2">
              <ArrowLeft size={13} /> Все кампании
            </button>
          )}
          <div className="font-display font-bold text-[18px]">
            {step.kind === "campaigns" ? "Добавить из кампании" : step.campaign.product}
          </div>
          {step.kind === "bloggers" && (
            <div className="text-[12px] text-ink-40 mt-0.5">
              Выберите блогеров для добавления в подборку
            </div>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-2">
          {step.kind === "campaigns" && (
            <>
              {eligible.length === 0 && (
                <div className="text-center text-ink-40 py-10 text-[13px]">Нет запущенных кампаний</div>
              )}
              {eligible.map((c) => (
                <button key={c.id}
                  onClick={() => { setStep({ kind: "bloggers", campaign: c }); setPicked(new Set()); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-md text-left hover:bg-ink-6 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[14px] truncate">{c.product}</div>
                    <div className="text-[12px] text-ink-40 mt-0.5">
                      {c.type === "seeding" ? "Посев" : "Натив"} · {c.platforms.join(", ")} · {c.placements_count} блогеров
                    </div>
                  </div>
                  <span className="text-ink-40 text-[12px] flex-shrink-0">→</span>
                </button>
              ))}
            </>
          )}

          {step.kind === "bloggers" && (
            <>
              {kanbanLoading && (
                <div className="grid gap-1 p-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <Skeleton className="w-5 h-5 rounded-sm flex-shrink-0" />
                      <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                      <div className="flex-1 flex flex-col gap-1.5">
                        <Skeleton className="h-3.5 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!kanbanLoading && campaignBloggers.map((b) => {
                const inCollection = currentMemberIds.has(b.id);
                const isSelected = picked.has(b.id);
                return (
                  <button key={b.id}
                    onClick={() => toggle(b.id)}
                    disabled={inCollection}
                    className={cn(
                      "flex items-center gap-3 w-full px-4 py-3 rounded-md text-left transition-colors",
                      inCollection ? "opacity-50 cursor-default" : "hover:bg-ink-6",
                    )}>
                    <div className={cn(
                      "w-5 h-5 rounded-sm border-2 grid place-items-center flex-shrink-0 transition-colors",
                      inCollection || isSelected ? "bg-lime border-lime" : "border-ink-12",
                    )}>
                      {(inCollection || isSelected) && (
                        <Check size={11} strokeWidth={3} className="text-[var(--fg-on-accent)]" />
                      )}
                    </div>
                    <Avatar name={b.full} size={32} grad={b.grad as [string, string]} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
                        <PlatformIcon platform={b.platform as Platform} size={11} />
                        {b.name}
                      </div>
                      <div className="text-[11.5px] text-ink-40 truncate">
                        {b.full} · охват {fmtNum(b.reach)}
                        {inCollection && <span className="ml-2 text-lime">уже в подборке</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        {step.kind === "bloggers" && (
          <div className="px-6 py-4 border-t border-ink-12 flex-shrink-0 flex items-center justify-between gap-3">
            <span className="text-[13px] text-ink-40">
              {picked.size > 0
                ? `Выбрано: ${picked.size} ${pluralBloggers(picked.size)}`
                : "Выберите блогеров"}
            </span>
            <div className="flex gap-2">
              <button onClick={onClose}
                className="h-9 px-4 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink">
                Отмена
              </button>
              <button onClick={addPicked} disabled={picked.size === 0 || add.isPending}
                className="h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[13px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] disabled:opacity-40">
                {add.isPending ? "Добавляем…" : `Добавить ${picked.size > 0 ? picked.size : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI brief panel ───────────────────────────────────────────────────────────

function AiBriefPanel({ brief }: { brief: AiBrief }) {
  const [open, setOpen] = useState(true);
  const Caret = open ? ChevronUp : ChevronDown;
  return (
    <Card className="!p-0 mb-5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3.5 bg-[var(--bg-2)] border-b border-ink-12 text-left">
        <div className="w-8 h-8 rounded-md bg-lime grid place-items-center text-[var(--fg-on-accent)] flex-shrink-0">
          <Sparkles size={15} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-[15px] tracking-tight">AI-разбор подборки</div>
          <div className="text-[11.5px] text-ink-40">
            {open ? "Что мы поняли о вашем бренде и как пришли к этому списку" : "Развернуть, чтобы посмотреть логику подбора"}
          </div>
        </div>
        {!brief.site_fetched && (
          <span className="text-[10.5px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded-pill bg-warning/15 text-warning">
            сайт не прочитан
          </span>
        )}
        <Caret size={16} className="text-ink-40 flex-shrink-0" />
      </button>

      {open && (
        <div className="p-5 grid gap-4 fade-up">
          <BriefSection
            icon={<Globe size={13} />}
            label="Что поняли по сайту"
            body={brief.site_understanding}
            muted={!brief.site_fetched}
          />
          <BriefSection
            icon={<Sparkles size={13} />}
            label="Бриф + сайт = задача РК"
            body={brief.brief_understanding}
          />
          <BriefSection
            icon={<Target size={13} />}
            label="Портрет целевой аудитории"
            body={brief.icp}
          />
          <BriefSection
            icon={<Wallet size={13} />}
            label={`Бюджет: ${fmtRubFull(brief.input.budget)}`}
            body={brief.budget_analysis}
          />

          <div className="border-t border-ink-12 pt-3 grid gap-1.5">
            <div className="text-[10.5px] uppercase tracking-wider text-ink-40 font-display font-bold">Ваш ввод</div>
            <div className="text-[12px] text-ink-60 leading-relaxed">
              <span className="text-ink-40">URL: </span><span className="font-mono">{brief.input.url}</span>
              {brief.input.geo.length > 0 && (
                <>
                  <span className="text-ink-40 ml-3">Гео: </span>
                  <span>{brief.input.geo.join(", ")}</span>
                </>
              )}
            </div>
            {brief.input.what && (
              <div className="text-[12px] text-ink-60 leading-relaxed">
                <span className="text-ink-40">Что рекламируем: </span>«{brief.input.what}»
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function BriefSection({ icon, label, body, muted }: {
  icon: React.ReactNode; label: string; body: string; muted?: boolean;
}) {
  return (
    <div className="grid grid-cols-[24px_1fr] gap-3">
      <div className={cn(
        "w-6 h-6 rounded-md grid place-items-center flex-shrink-0 mt-0.5",
        muted ? "bg-ink-6 text-ink-40" : "bg-ink-6 text-ink-60",
      )}>
        {icon}
      </div>
      <div>
        <div className={cn(
          "text-[10.5px] uppercase tracking-wider font-display font-bold mb-1",
          muted ? "text-ink-40" : "text-ink-60",
        )}>
          {label}
        </div>
        <div className={cn(
          "text-[13.5px] leading-relaxed",
          muted ? "text-ink-40 italic" : "text-ink",
        )}>
          {body}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Stat({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <Card className="!p-3.5">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-ink-40 font-display font-semibold">
        {icon} {label}
      </div>
      <div className={`font-display font-extrabold text-[20px] mt-1 tabular-nums ${accent ? "text-success" : ""}`}>{value}</div>
    </Card>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <div className="font-mono font-bold text-[13px] tabular-nums">{value}</div>
      <div className="text-[10.5px] text-ink-40 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function pluralBloggers(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "блогер";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "блогера";
  return "блогеров";
}
